import type React from 'react';
import { useEffect, useLayoutEffect, useReducer, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { FileUploadStore, getFilesFromEvent, isRejectedType } from './FileUploadStore';
import type { DraggedItem, DropzoneExtra, FileWithMeta, GetUploadParams, OnChangeStatus } from './types';

export interface UseFileUploadsOptions {
  /** An `<input accept>` string. A file that does not match ends in `rejected_file_type`. */
  accept?: string;
  /** Sets the input's `multiple` attribute only: a drop always takes every file. */
  multiple?: boolean;
  minSizeBytes?: number;
  maxSizeBytes?: number;
  maxFiles?: number;
  /** Without it, a file goes from `preparing` straight to `done` and nothing is sent. */
  getUploadParams?: GetUploadParams;
  onChangeStatus?: OnChangeStatus;
}

type Handler<E> = ((event: E) => void) | undefined;

export interface UseFileUploadsResult {
  files: FileWithMeta[];
  extra: DropzoneExtra;
  rootProps: {
    ref: React.RefObject<HTMLElement>;
    onDragEnter: Handler<React.DragEvent<HTMLElement>>;
    onDragOver: Handler<React.DragEvent<HTMLElement>>;
    onDragLeave: Handler<React.DragEvent<HTMLElement>>;
    onDrop: Handler<React.DragEvent<HTMLElement>>;
  };
  inputProps: {
    ref: React.RefObject<HTMLInputElement>;
    onChange: Handler<React.ChangeEvent<HTMLInputElement>>;
  };
}

/**
 * Takes files from a drop or a file input and uploads each one with its own XMLHttpRequest, built
 * from `getUploadParams`. Every status change reaches `onChangeStatus` with the live `FileWithMeta`.
 * Uploads still in flight are aborted on unmount.
 */
export const useFileUploads = ({
  accept = '*',
  multiple = true,
  minSizeBytes = 0,
  maxSizeBytes = Number.MAX_SAFE_INTEGER,
  maxFiles = Number.MAX_SAFE_INTEGER,
  getUploadParams,
  onChangeStatus,
}: UseFileUploadsOptions): UseFileUploadsResult => {
  const options = { accept, minSizeBytes, maxSizeBytes, maxFiles, getUploadParams, onChangeStatus };
  const [store] = useState(() => new FileUploadStore(options));
  const [dragged, setDragged] = useState<DraggedItem[]>([]);

  const { getRootProps, getInputProps, inputRef, isDragActive, rootRef } = useDropzone({
    getFilesFromEvent: async event => getFilesFromEvent(event),
    // Accept, size and count rules run in the store, so react-dropzone passes every file through.
    multiple: true,
    // The <label> around the input opens the file picker; the root itself is not a control.
    noClick: true,
    noKeyboard: true,
    noPaste: true,
    noDragEventsBubbling: true,
    preventDropOnDocument: false,
    onDrop: (acceptedFiles, _rejections, event) => {
      store.addFiles(acceptedFiles, rootRef.current);
      // Clearing the input lets the same file be chosen again.
      const target = (event as Event | undefined)?.target;
      if (target instanceof HTMLInputElement) target.value = '';
    },
  });

  // The store calls the latest callbacks, the way a class component reads this.props.
  useLayoutEffect(() => {
    store.options = options;
  });

  // A plain subscription renders through the scheduler, batching a burst of progress events into
  // one render. useSyncExternalStore would render synchronously on each one.
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const unsubscribe = store.subscribe(rerender);
    return () => {
      unsubscribe();
      store.cancelAll();
    };
  }, [store]);

  const files = store.getSnapshot();

  const root = getRootProps({
    // Read synchronously: a drag event's item list is empty once the handler returns.
    onDragEnter: event => setDragged(getFilesFromEvent(event)),
  }) as Record<string, Handler<React.DragEvent<HTMLElement>>>;
  const input = getInputProps() as { onChange?: Handler<React.ChangeEvent<HTMLInputElement>> };

  return {
    files,
    extra: {
      active: isDragActive,
      reject: isDragActive && dragged.some(item => isRejectedType(item, accept)),
      dragged: isDragActive ? dragged : [],
      accept,
      multiple,
      minSizeBytes,
      maxSizeBytes,
      maxFiles,
    },
    rootProps: {
      ref: rootRef,
      onDragEnter: root.onDragEnter ?? undefined,
      onDragOver: root.onDragOver ?? undefined,
      onDragLeave: root.onDragLeave ?? undefined,
      onDrop: root.onDrop ?? undefined,
    },
    inputProps: {
      ref: inputRef,
      onChange: input.onChange ?? undefined,
    },
  };
};
